/**
 * Time display helpers — canonical storage/API remains 24h (HH:mm / HH:mm:ss).
 * Use these for user-facing 12-hour (en-US) strings only.
 */

function normalizeDbTimeParts(timeStr) {
  if (timeStr == null || timeStr === '') return null;
  const s = String(timeStr).trim();
  const m = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return null;
  const hours = parseInt(m[1], 10);
  const minutes = parseInt(m[2], 10);
  if (Number.isNaN(hours) || Number.isNaN(minutes) || hours > 23 || minutes > 59) return null;
  return { hours, minutes };
}

/** Format DB TIME string (24h) as 12h locale string, e.g. "1:44 PM". */
export function formatTime12hFromDb(timeStr) {
  const parts = normalizeDbTimeParts(timeStr);
  if (!parts) return 'N/A';
  const d = new Date(2000, 0, 1, parts.hours, parts.minutes, 0);
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/** `HH:mm` suitable for `<input type="time" />` from DB `HH:mm` or `HH:mm:ss`. */
export function dbTimeToInput24h(timeStr) {
  const parts = normalizeDbTimeParts(timeStr);
  if (!parts) return '';
  return `${String(parts.hours).padStart(2, '0')}:${String(parts.minutes).padStart(2, '0')}`;
}

/** Parse hour (1–12), minute (0–59), period AM|PM → `HH:mm` (24h). */
export function parse12hClockTo24h(hour12, minute, period) {
  let h = Number(hour12);
  const m = Number(minute);
  const p = String(period || '').toUpperCase();
  if (Number.isNaN(h) || Number.isNaN(m) || h < 1 || h > 12 || m < 0 || m > 59) return '';
  if (p === 'AM') {
    if (h === 12) h = 0;
  } else if (p === 'PM') {
    if (h !== 12) h += 12;
  } else {
    return '';
  }
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Split `HH:mm` (24h) into { hour12, minute, period } for 12h controls. */
export function split24hTo12hParts(hhmm) {
  const parts = normalizeDbTimeParts(hhmm);
  if (!parts) return { hour12: 9, minute: 0, period: 'AM' };
  let h = parts.hours;
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return { hour12, minute: parts.minutes, period };
}

/** Full date-time for messages, deadlines, etc. */
export function formatDateTime12h(value) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return 'N/A';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/** Time-only portion of a Date in 12h. */
export function formatDateToTime12h(value) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return 'N/A';
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}
